import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { XIcon } from '../../../assets/X';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from '../../../components/ui/drawer';
import { cn } from '../../../lib/utils';
import { userInfo } from '../../../store/selectors/user';
import { useGetMindboxUserInfo } from '../../home/services/getMindboxUserInfo';
import { Skeleton } from '../../../components/ui/skeleton';
import { loyaltyQrPlaceholderKey, useLoyaltyQr } from '../services/useLoyaltyQr';
import { BonusHistoryDrawer } from './BonusHistoryDrawer';
import { LoyaltyInfoButton, LoyaltyInfoDrawer } from './LoyaltyInfoDrawer';
import { QrCountdown } from './QrCountdown';
import { QrLoader } from './QrLoader';
import { StyledQr } from './StyledQr';
import coinFullImg from './assets/coin-full.png';

type LoyaltyQrDrawerProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

/**
 * Шторка с QR лояльности — одна на все входы (главная, профиль).
 *
 * Свёрстана по макету 1661-34599: размеры и отступы — оттуда, в пикселях.
 * Сам код от Mindbox не зависит: его выдаёт `/client/loyalty/qr`, а касса по
 * нему получает телефон. Mindbox нужен только для баланса и истории — при
 * выключенном флаге код показываем, а баллы не спрашиваем и не рисуем.
 */
export const LoyaltyQrDrawer = ({ open, setOpen }: LoyaltyQrDrawerProps) => {
  const { t } = useTranslation();
  const user = useSelector(userInfo);
  const { availablePoints, isLoading: isBalanceLoading } = useGetMindboxUserInfo(
    // Не зависит от флага mindbox в /settings: карта с QR показывается всегда,
    // и баланс на ней должен быть настоящим, а не нулём. Флаг решает только
    // списание баллов в корзине.
    user.isAuth
  );
  const qr = useLoyaltyQr(open);
  const placeholderKey = loyaltyQrPlaceholderKey(qr.mode);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  // История — не ещё одна шторка поверх, а следующий экран: QR закрываем,
  // «назад» возвращает к нему. Две открытые шторки складывали оверлеи
  // (80% + 80%), и фон за ними становился чёрным.
  const openHistory = () => {
    setInfoOpen(false);
    setOpen(false);
    setHistoryOpen(true);
  };
  const backToQr = () => {
    setHistoryOpen(false);
    setOpen(true);
  };

  const isOffline = qr.mode === 'static' || qr.mode === 'offline-no-id';

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={next => {
          if (!next) setInfoOpen(false);
          setOpen(next);
        }}
        shouldScaleBackground={false}
      >
        <DrawerContent
          handleClassName="hidden"
          // Офлайн-экран в макете на серой подложке с белой карточкой, без
          // зелёной дымки; основной — на белой с дымкой.
          className={cn(
            'overflow-hidden border-none',
            isOffline ? 'bg-fill-background' : 'bg-white'
          )}
        >
          {isOffline ? (
            // Офлайн-экран по макету 2034-30537: заголовок, крестик, подсказка
            // и статичный код. Ни таймера, ни баланса, ни истории — без сети
            // им неоткуда взяться; кнопки «Проверить подключение» в WebApp
            // нет по постановке: сеть отслеживается сама.
            <div className="relative flex flex-col">
              <div className="flex h-12 items-center justify-between px-4 pb-2 pt-4">
                <DrawerTitle className="text-display-md text-text-primary">
                  {t('no-network-title')}
                </DrawerTitle>
                <div className="flex items-center gap-2">
                  <LoyaltyInfoButton onClick={() => setInfoOpen(true)} />
                  <button
                    type="button"
                    aria-label={t('cancel')}
                    onClick={() => setOpen(false)}
                    className="flex h-6 w-6 items-center justify-center rounded-full bg-white"
                  >
                    <XIcon className="h-2.5 w-2.5 text-text-icon-secondary" />
                  </button>
                </div>
              </div>

              <p className="mx-auto max-w-[335px] px-2 pt-6 text-center text-[12px] font-normal leading-[14px] text-text-tertiary">
                {t('offline-qr-text')}
              </p>

              {/* Ширина задана явно (w-full внутри px-4), а не через mx-4 на
                  растянутом flex-элементе: Safari у такого элемента с
                  aspect-square берёт размер от содержимого — карточка с кодом
                  вылезала за экран, и у шторки появлялся горизонтальный
                  скролл. Та же схема, что у карточки онлайн-экрана. */}
              <div className="px-4">
                <div className="mt-3 flex aspect-square w-full min-w-0 items-center justify-center overflow-hidden rounded-[28px] bg-white p-9">
                  {qr.value ? (
                    <StyledQr value={qr.value} size={271} />
                  ) : (
                    <p className="text-center text-body-sm-regular text-text-secondary">
                      {placeholderKey ? t(placeholderKey) : ''}
                    </p>
                  )}
                </div>
              </div>

              <div className="pb-safe-bottom pt-10" />
            </div>
          ) : (
          <>
          {/* Два пятна из макета: #2bb747 сверху справа, #007854 снизу
              слева, 200×318, opacity 0.72, layer blur 300 — «зелёная дымка»
              за контентом. Радиус блюра в Figma — диаметр гауссианы, в CSS —
              сигма, поэтому 150. Привязаны к углам, а не к левому краю:
              на широком экране пятна должны остаться в углах, а не уехать
              к центру вместе с координатами макета на 375. */}
          <div
            aria-hidden
            className="pointer-events-none absolute right-[-60px] top-[-73px] h-[318px] w-[200px] rounded-full bg-brand-green opacity-[0.72] blur-[150px]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-[-73px] left-[-74px] h-[318px] w-[200px] rounded-full bg-[#007854] opacity-[0.72] blur-[150px]"
          />

          <div className="relative flex flex-col">
            {/* Section Title: pad 16/16/8, строка 24. */}
            <div className="flex h-12 items-center justify-between px-4 pb-2 pt-4">
              <DrawerTitle className="text-display-md text-text-primary">
                {t('scan-qr-title')}
              </DrawerTitle>
              <button
                type="button"
                aria-label={t('cancel')}
                onClick={() => setOpen(false)}
                className="flex h-6 w-6 items-center justify-center rounded-full bg-white"
              >
                <XIcon className="h-2.5 w-2.5 text-text-icon-secondary" />
              </button>
            </div>

            {/* QR Section: между блоками 12. Над карточкой — подсказка про
                кешбэк тем же шрифтом, что на офлайн-экране; она занимает
                бывший отступ 45 до карточки, чтобы шторка почти не выросла. */}
            <div className="flex flex-col gap-3 px-4 pt-4">
              <p className="mx-auto max-w-[335px] px-2 text-center text-[12px] font-normal leading-[14px] text-text-tertiary">
                {t('dynamic-qr-text')}
              </p>

              {/* Карточка кода из макета: r28, рамка 1px, отступ 36. Ширина —
                  экран минус 16 с каждой стороны (343 на 375), квадрат: код
                  внутри тянется вместе с ней. */}
              <div className="flex aspect-square w-full min-w-0 items-center justify-center overflow-hidden rounded-[28px] border border-white bg-white/[0.72] p-9">
                {qr.value ? (
                  <StyledQr value={qr.value} size={271} />
                ) : qr.mode === 'loading' ? (
                  <QrLoader labelKey={placeholderKey} />
                ) : (
                  <p className="text-center text-body-sm-regular text-text-secondary">
                    {placeholderKey ? t(placeholderKey) : ''}
                  </p>
                )}
              </div>

              {qr.mode === 'dynamic' && qr.expiresAtMs && qr.lifetimeMs ? (
                <QrCountdown
                  expiresAtMs={qr.expiresAtMs}
                  lifetimeMs={qr.lifetimeMs}
                />
              ) : (
                // Держим место под таймер (28px), чтобы баланс не прыгал при
                // смене динамического кода на статичный и обратно.
                <div className="h-7" />
              )}

              {/* Weight из макета: h74, r999, pad 8/24/8/16, gap 16, фон
                  #FFFFFFB8; по ширине — как карточка кода. Рисуется всегда —
                  при выключенном Mindbox баланс просто не запрашивается и
                  остаётся 0. */}
              <div className="flex h-[74px] w-full items-center gap-4 rounded-[999px] bg-[#FFFFFFB8] py-2 pl-4 pr-6">
                {/* Монета по макету: картинка с полями 56.09×58.69, повёрнута
                    на 25° против часовой, по центру слота 48×48 и вылезает
                    за него — как на карточке на главной. */}
                <span aria-hidden className="relative h-12 w-12 shrink-0">
                  <img
                    src={coinFullImg}
                    alt=""
                    className="absolute left-1/2 top-1/2 h-[58.69px] w-[56.09px] max-w-none -translate-x-1/2 -translate-y-1/2 -rotate-[25deg]"
                  />
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  <p className="font-inter-tight text-caption-sm-medium text-text-tertiary">
                    {t('balance')}
                  </p>
                  {isBalanceLoading ? (
                    <Skeleton className="mt-1 h-7 w-32 rounded-lg bg-black/10" />
                  ) : (
                    <p className="text-display-md text-text-primary">
                      {availablePoints.toLocaleString('ru-RU')}{' '}
                      <span className="text-brand-green">E</span>-coins
                    </p>
                  )}
                </div>
                <LoyaltyInfoButton onClick={() => setInfoOpen(true)} />
              </div>
            </div>

            {/* FixedButton: отступ 45 от баланса, pad 12/16/32/16, фон
                white@0.8 с верхними углами 14. Кнопка 343×48, r32, px 20,
                фон fill-background. */}
            <div className="mt-[45px] rounded-t-[14px] bg-white/80 px-4 pb-8 pt-3">
              <button
                type="button"
                onClick={openHistory}
                className="mx-auto flex h-12 w-[343px] max-w-full items-center justify-center gap-2 rounded-[32px] bg-fill-background px-5 font-inter text-[14px] font-semibold leading-5 text-text-primary"
              >
                {t('points-history')}
              </button>
            </div>
          </div>
          </>
          )}

          {/* Вложенная шторка должна жить внутри корня QR-шторки. */}
          <LoyaltyInfoDrawer open={infoOpen} setOpen={setInfoOpen} />
        </DrawerContent>
      </Drawer>

      <BonusHistoryDrawer
        open={historyOpen}
        setOpen={setHistoryOpen}
        onBack={backToQr}
      />
    </>
  );
};
